import type { Root, Code } from "mdast";
import { visit } from "unist-util-visit";

import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

import type { ContentJsonObject, ErrorJsonObject, ParsedContentCommand } from "../types/content";
import { isContentJsonObject, isErrorJsonObject, isParsedContentCommand } from "../types/content.guards";

export type ExtractedJsonData = {
    readonly commands: readonly ParsedContentCommand[];
    readonly jsonObjects: readonly ContentJsonObject[];
};

/**
 * Remark plugin to extract and categorize JSON code blocks from markdown content.
 * This plugin visits all code blocks with language 'json' and extracts command data.
 */
export const remarkJsonExtractPlugin = function () {
    return (tree: Root, content: { data: Record<string, unknown> }) => {
        let commands: readonly ParsedContentCommand[] = [];
        let jsonObjects: readonly (ContentJsonObject | ErrorJsonObject)[] = [];

        visit(tree, "code", (node: Code) => {
            if (node.lang !== "json" || !node.value) {
                return;
            }

            try {
                const rawJsonText = node.value;
                const parsed: unknown = JSON.parse(rawJsonText);

                const objects = Array.isArray(parsed) ? parsed : [parsed];

                objects.forEach((jsonObj: unknown) => {
                    if (!isContentJsonObject(jsonObj) && !isErrorJsonObject(jsonObj)) {
                        return;
                    }

                    // Store ALL JSON objects for render (components decide what to show)
                    jsonObjects = [...jsonObjects, jsonObj];

                    if (isParsedContentCommand(jsonObj)) {
                        commands = [...commands, jsonObj];
                    }
                });
            } catch (e) {
                sentryErrorTrackerWithScope({
                    errorMessage: "Error parsing JSON code block in remark plugin",
                    e: e,
                    params: {
                        extra: {
                            codeContent: node.value,
                        },
                    },
                });
            }
        });

        content.data.extractedJson = {
            commands,
            jsonObjects,
        };
    };
};
